import React, { ReactElement } from "react";
import { PDFType, PDFFactory, PDFContext, PDFRegistry } from "./types";

// Import PDF components
import { CareerPlanPDF } from "./CareerPlanPDF";
import { CareerPlanPDFWithSvgConversion } from "./CareerPlanPDFWithSvgConversion";
import { CoursePlanPDF } from "./CoursePlanPDF";
import { ClimbPlanPDF } from "./ClimbPlanPDF";
// import { CertificatePDF } from './CertificatePDF'; // TODO: Implement CertificatePDF component

// PDF Factory Registry
const pdfRegistry: Partial<PDFRegistry> = {
  [PDFType.CAREER_PLAN]: {
    createPDF: (context: PDFContext) => (
      <CareerPlanPDFWithSvgConversion {...context} />
    ),
    getConfig: () => ({
      title: "Career Plan",
      fileName: "career_plan.pdf",
      pageSize: "LETTER",
      orientation: "portrait",
    }),
    validateData: (data: any) => {
      return data && data.user && data.pathway && data.hat;
    },
  },
  [PDFType.COURSE_PLAN]: {
    createPDF: (context: PDFContext) => <CoursePlanPDF data={context.data} />,
    getConfig: () => ({
      title: "Course Plan",
      fileName: "course_plan.pdf",
      pageSize: "LETTER",
      orientation: "portrait",
    }),
    validateData: (data: any) => {
      return data && data.climber && data.pathway;
    },
  },
  [PDFType.CLIMB_PLAN]: {
    createPDF: (context: PDFContext) => <ClimbPlanPDF data={context.data} />,
    getConfig: () => ({
      title: "Climb Plan",
      fileName: "climb_plan.pdf",
      pageSize: "LETTER",
      orientation: "portrait",
    }),
    // Deliberately two fields. A `false` here returns null all the way up to
    // the button, which then sits permanently disabled with nothing on screen
    // to say why — so this checks that a plan exists at all, not that every
    // optional section was filled in.
    validateData: (data: any) => !!data?.climber?.name && !!data?.topHat,
  },
  // TODO: Uncomment when CertificatePDF component is implemented
  // [PDFType.CERTIFICATE]: {
  //   createPDF: (context: PDFContext) => <CertificatePDF {...context} />,
  //   getConfig: () => ({
  //     title: 'Certificate',
  //     fileName: 'certificate.pdf',
  //     pageSize: 'A4',
  //     orientation: 'landscape',
  //   }),
  //   validateData: (data: any) => {
  //     return data && data.award && data.currentUser;
  //   },
  // },
};

// Main PDF Factory Class
export class PDFFactoryManager {
  private static instance: PDFFactoryManager;
  private registry: Partial<PDFRegistry>;

  private constructor() {
    this.registry = pdfRegistry;
  }

  public static getInstance(): PDFFactoryManager {
    if (!PDFFactoryManager.instance) {
      PDFFactoryManager.instance = new PDFFactoryManager();
    }
    return PDFFactoryManager.instance;
  }

  // Register a new PDF type
  public registerPDFType(type: PDFType, factory: PDFFactory): void {
    this.registry[type] = factory;
  }

  // Get PDF factory for a specific type
  public getPDFFactory(type: PDFType): PDFFactory | null {
    return this.registry[type] || null;
  }

  // Create PDF component
  public createPDF(type: PDFType, context: PDFContext): ReactElement | null {
    const factory = this.getPDFFactory(type);
    if (!factory) {
      return null;
    }

    if (!factory.validateData(context.data)) {
      return null;
    }

    return factory.createPDF(context);
  }

  // Get default config for a PDF type
  public getPDFConfig(type: PDFType) {
    const factory = this.getPDFFactory(type);
    return factory?.getConfig() || null;
  }

  // Validate data for a specific PDF type
  public validatePDFData(type: PDFType, data: any): boolean {
    const factory = this.getPDFFactory(type);
    return factory?.validateData(data) || false;
  }

  // Get all available PDF types
  public getAvailablePDFTypes(): PDFType[] {
    return Object.keys(this.registry) as PDFType[];
  }
}

// Export singleton instance
export const pdfFactory = PDFFactoryManager.getInstance();
